/**
 * HLOS-51 — BiometricKeyManager
 *
 * Cifra/decifra a master key do vault TS-side usando chaves vinculadas a
 * biometria (Secure Enclave iOS / Keystore Android / mock em dev).
 *
 * Arquitetura:
 *   1. Setup: gera ECDH P-256 keypair. Cifra master key com AES-GCM usando
 *      shared secret derivado. Salva {public_key, encrypted_master_key, iv}
 *      em OPFS (vault.store.biometric).
 *   2. Unlock: prompt biometric → decifra blob → master key em memória.
 *   3. Fallback (CC-05): 3+ falhas biométricas → passphrase.
 *
 * Constitucional:
 * - CL-07 (local-first): chaves biométricas nunca saem do device.
 * - CL-08 (Ética fechada): master key NUNCA exportada em plaintext.
 * - CC-05 (zero penalidade): 3+ falhas biométricas → fallback passphrase.
 * - Princípio X (Dignidade): copy neutra.
 * - Princípio V (Sistema evolui, Aprendiz decide): opt-in voluntário.
 *
 * Refs:
 * - HLOS-42.2 (Sprint 4: BiometricStore/Toggle base)
 * - HLOS-48 (master key via HLOS-48 ts-side vault)
 * - HLOS-51 (este módulo: master key cifrada com biometric-bound key)
 * - ADR-0004 (OPFS + WebCrypto + Argon2)
 * - ADR-0015 (dual-key wrapping)
 */

import { authenticate } from "./service";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import type {
  PluginAuthenticateOptions,
  BiometricDecision,
} from "./types";

// ─────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────

/**
 * Blob cifrado armazenado em OPFS (vault.store.biometric).
 *
 * Schema versionado (HLOS-49 estilo). Bump em migration incompatível.
 */
export interface EncryptedMasterKeyBlob {
  readonly version: 1;
  /** Chave pública ECDH P-256 (raw, 65 bytes uncompressed). Base64. */
  readonly publicKey: string;
  /** Master key cifrada com AES-256-GCM (32+12+16 bytes). Base64. */
  readonly encryptedMasterKey: string;
  /** IV AES-GCM (12 bytes). Base64. */
  readonly iv: string;
  /** Unix epoch (ms) — para diagnóstico / versão. */
  readonly createdAt: number;
  /** Nome do biometria (ex: "TouchID", "FaceID") para UX. */
  readonly biometricType: string;
}

/**
 * Resultado de unlock biométrico.
 */
export type BiometricUnlockResult =
  | { readonly outcome: "biometric_ok"; readonly masterKey: Uint8Array }
  | { readonly outcome: "passphrase" }
  | { readonly outcome: "canceled" };

/**
 * Counter de falhas para CC-05 (3+ falhas → fallback).
 */
const MAX_FAILURES_BEFORE_FALLBACK = 3;

// ─────────────────────────────────────────────────────────────────────
// Module-level state (failure counter)
// ─────────────────────────────────────────────────────────────────────

let consecutiveFailures = 0;

// ─────────────────────────────────────────────────────────────────────
// Helpers (encoding + crypto)
// ─────────────────────────────────────────────────────────────────────

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function importEcdhPublicKey(rawPublicKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    // Cast: TS lib mismatch (Uint8Array<ArrayBufferLike> vs BufferSource expects ArrayBuffer).
    // Vault type-safety is enforced at the typed store layer; aquí aceitamos o cast.
    rawPublicKey as BufferSource,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
}

async function exportEcdhPublicKey(publicKey: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey("raw", publicKey));
}

async function deriveSharedSecret(
  privateKey: CryptoKey,
  publicKey: CryptoKey,
): Promise<Uint8Array> {
  const bits = await crypto.subtle.deriveBits(
    { name: "ECDH", public: publicKey },
    privateKey,
    256,
  );
  return new Uint8Array(bits);
}

async function aesGcmEncrypt(
  key: CryptoKey,
  iv: Uint8Array,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  return new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, plaintext as BufferSource),
  );
}

async function aesGcmDecrypt(
  key: CryptoKey,
  iv: Uint8Array,
  ciphertext: Uint8Array,
): Promise<Uint8Array> {
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, ciphertext as BufferSource),
  );
}

async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", rawKey as BufferSource, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

// ─────────────────────────────────────────────────────────────────────
// Storage (OPFS via vault.store.biometric)
// ─────────────────────────────────────────────────────────────────────

/**
 * HLOS-176: blob biométrico salvo em SecureStorage (Keychain iOS com
 * Secure Enclave / Keystore Android com TEE), NÃO dentro do vault cifrado
 * E NÃO em Preferences (UserDefaults plaintext).
 *
 * Por que fora do vault cifrado: o unlock biométrico precisa LER o blob
 * antes do vault estar unlocked (locked mode → Face ID prompt → decifra
 * blob → unlock vault). Se blob estivesse dentro do vault cifrado,
 * teríamos dependência circular (precisa desbloquear vault pra ler blob
 * que destrava o vault).
 *
 * Por que SecureStorage (não Preferences): UserDefaults é plaintext em
 * disco — jailbreak ou backup extraction lê blob + private key. Keychain
 * cifra via hardware (Secure Enclave iOS / Keystore Android com TEE).
 *
 * Constitucional:
 * - CL-07 (vault soberano): hardware-bound, plataforma nunca tem acesso.
 * - CL-08 (sem key escrow): chave privada NUNCA plaintext em disco.
 *
 * LGPD: blob contém apenas dados cifrados biometricamente. Sem PII.
 */
const BLOB_STORAGE_KEY = "hlos.biometric.blob.v1";

/**
 * HLOS-174: persistência do blob biométrico usa `VaultAPI` real
 * (apps/ui-shell/src/vault/adapter.ts) em vez do singleton de test
 * `window.__hlosVaultStore`. Resolve o gap onde, em produção, o singleton
 * nunca era setado e `saveBlob` throw.
 *
 * Pré-HLOS-174 (HLOS-51 a HLOS-172): usava singleton `window.__hlosVaultStore`
 * setado apenas em testes via `setVaultStoreForTesting()`. Em runtime,
 * `getVaultStore()` sempre retornava null → saveBlob throw
 * "[HLOS-51] vault store não disponível".
 *
 * HLOS-174: `getVaultAPI().readData()` / `writeData()` é o boundary type-safe
 * (HLOS-134). A cifragem do blob acontece **antes** (AES-GCM sobre master key);
 * VaultAPI persiste o blob cifrado no backend (OPFS / Capacitor iOS bridge / memory).
 *
 * Constitucional:
 * - CL-07 (vault soberano): VaultAPI é local-first.
 * - CL-08 (sem key escrow): VaultAPI persiste apenas bytes cifrados.
 */

/**
 * @deprecated desde HLOS-174. O singleton `window.__hlosVaultStore` não é mais
 * usado pelo runtime. Mantido como stub vazio para não quebrar HLOS-51+ tests
 * pré-existentes (cleanup em HLOS-175+). Use `setVaultAPI(mockAPI)` em testes.
 */
export function setVaultStoreForTesting(_store: unknown): void {
  // No-op: deprecated. Tests devem usar `setVaultAPI(mockAPI)` (vault/adapter.ts:36).
}

/**
 * @deprecated desde HLOS-174. Idem — use `getVaultAPI()` (vault/adapter.ts:49).
 */
export function setVaultStoreForTestingClear(): void {
  // No-op: deprecated.
}

// ─────────────────────────────────────────────────────────────────────
// Cache de private key (in-memory + SecureStorage para sobreviver force quit)
// ─────────────────────────────────────────────────────────────────────
//
// HLOS-176: private key ECDH persistida em **SecureStorage** (Keychain iOS
// com Secure Enclave / Android Keystore com TEE). Cifrada pelo hardware —
// nunca plaintext em disco.
//
// Por que SecureStorage (não Preferences): em HLOS-176v3 WIP a private key
// JWK ficava em @capacitor/preferences (UserDefaults plaintext) —
// jailbreak ou backup extraction permitia decifrar o blob biométrico sem
// Face ID. SecureStorage resolve o gap CL-08.
//
// Migrado de "HLOS-177" → HLOS-176 (era expectativa de fix futuro; agora
// é parte do HLOS-176 final).

const privateKeyCache: Map<string, CryptoKey> = new Map();
const PRIV_KEY_STORAGE_PREFIX = "hlos.biometric.privkey.";

function getPrivKeyStorageKey(publicKeyBase64: string): string {
  return `${PRIV_KEY_STORAGE_PREFIX}${publicKeyBase64}`;
}

async function persistPrivateKey(
  publicKeyBase64: string,
  privateKey: CryptoKey,
): Promise<void> {
  try {
    const jwk = await crypto.subtle.exportKey("jwk", privateKey);
    await SecureStorage.set(getPrivKeyStorageKey(publicKeyBase64), JSON.stringify(jwk));
  } catch (e) {
    console.warn("[biometric] persistPrivateKey failed:", e);
  }
}

async function loadPrivateKey(
  publicKeyBase64: string,
): Promise<CryptoKey | null> {
  try {
    // SecureStorage.get() retorna DataType | null diretamente (não {value}).
    const value = await SecureStorage.get(getPrivKeyStorageKey(publicKeyBase64));
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") return null;
    const jwk = JSON.parse(value) as JsonWebKey;
    return await crypto.subtle.importKey(
      "jwk",
      jwk,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveBits"],
    );
  } catch (e) {
    console.warn("[biometric] loadPrivateKey failed:", e);
    return null;
  }
}

async function clearPersistedPrivateKey(publicKeyBase64: string): Promise<void> {
  try {
    await SecureStorage.remove(getPrivKeyStorageKey(publicKeyBase64));
  } catch (e) {
    console.warn("[biometric] clearPersistedPrivateKey failed:", e);
  }
}

function cachePrivateKey(publicKeyBase64: string, privateKey: CryptoKey): void {
  privateKeyCache.set(publicKeyBase64, privateKey);
}

async function cachePrivateKeyAsync(
  publicKeyBase64: string,
  privateKey: CryptoKey,
): Promise<void> {
  cachePrivateKey(publicKeyBase64, privateKey);
  await persistPrivateKey(publicKeyBase64, privateKey);
}

async function getCachedPrivateKey(
  publicKeyBase64: string,
): Promise<CryptoKey | undefined> {
  // 1. Tenta memory cache primeiro (mais rápido).
  const cached = privateKeyCache.get(publicKeyBase64);
  if (cached) return cached;

  // 2. Fallback: carrega de SecureStorage (sobrevive force quit).
  const loaded = await loadPrivateKey(publicKeyBase64);
  if (loaded) {
    privateKeyCache.set(publicKeyBase64, loaded);
    return loaded;
  }
  return undefined;
}

function clearCachedPrivateKey(publicKeyBase64: string): void {
  privateKeyCache.delete(publicKeyBase64);
}

async function clearCachedPrivateKeyAsync(
  publicKeyBase64: string,
): Promise<void> {
  clearCachedPrivateKey(publicKeyBase64);
  await clearPersistedPrivateKey(publicKeyBase64);
}

/** Para testing: limpa todo o cache (memory only). */
export function clearPrivateKeyCacheForTesting(): void {
  privateKeyCache.clear();
}

/**
 * HLOS-176: `loadBlob` agora usa Keychain iOS / Keystore Android via
 * SecureStorage — fora do vault cifrado E com cifragem hardware-backed.
 * Permite unlock biométrico quando vault está locked (corrige dependência
 * circular HLOS-174 sem violar CL-08 como HLOS-176v3).
 */
async function loadBlob(): Promise<EncryptedMasterKeyBlob | null> {
  try {
    // SecureStorage.get() retorna DataType | null diretamente.
    const value = await SecureStorage.get(BLOB_STORAGE_KEY);
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") return null;
    const blob = JSON.parse(value) as EncryptedMasterKeyBlob;
    if (!blob || blob.version !== 1) return null;
    return blob;
  } catch (e) {
    console.warn("[HLOS-176] loadBlob failed:", e);
    return null;
  }
}

/**
 * HLOS-176: `saveBlob` agora usa SecureStorage (Keychain/TEE).
 * Blob já é cifrado pela biometric private key + AES-GCM.
 */
async function saveBlob(blob: EncryptedMasterKeyBlob): Promise<void> {
  try {
    await SecureStorage.set(BLOB_STORAGE_KEY, JSON.stringify(blob));
  } catch (e) {
    console.error("[HLOS-176] saveBlob failed:", e);
    throw e;
  }
}

/**
 * HLOS-176: `clearBlob` agora usa SecureStorage.
 */
async function clearBlob(): Promise<void> {
  try {
    await SecureStorage.remove(BLOB_STORAGE_KEY);
  } catch (e) {
    console.warn("[HLOS-176] clearBlob failed:", e);
  }
}

/**
 * HLOS-176: cleanup de dados legacy de HLOS-176v3 WIP (Preferences plaintext).
 *
 * Contexto: HLOS-176v3 (commit `8ea3431`) salvava blob biométrico + private
 * key JWK em `@capacitor/preferences` (UserDefaults iOS plaintext). Esta
 * versão HLOS-176 final migra para SecureStorage (Keychain/TEE).
 *
 * Estratégia (Opção A do plano): NÃO migrar dados legacy — wipe. Razões:
 * 1. Key material em Preferences plaintext NUNCA pode ser copiado para
 *    Keychain — seria copiar dados inseguro para storage seguro sem
 *    re-autenticar (viola CL-08).
 * 2. User precisa re-configurar biométrico via BiometricToggle na Home
 *    (≤30s, transparente). Sem perda de dados (vault cifrado intacto).
 *
 * Retorna `true` se algum dado legacy foi removido (sinaliza ao caller para
 * resetar `hasBlob` flag também — coerência de estado).
 *
 * Idempotente — seguro chamar múltiplas vezes. Silent fail (best-effort):
 * se Preferences indisponível, ignora e retorna `false`.
 *
 * Constitucional:
 * - CL-08: zero key material plaintext persistido (precaução defensiva).
 */
export async function wipeLegacyBiometricDataFromPreferences(): Promise<boolean> {
  let wiped = false;
  try {
    // Import lazy para preservar bundle size (HLOS-131).
    const { Preferences } = await import("@capacitor/preferences");

    // 1. Wipe legacy blob em Preferences (mesma key, storage antigo).
    try {
      const { value } = await Preferences.get({ key: BLOB_STORAGE_KEY });
      if (value !== null) {
        await Preferences.remove({ key: BLOB_STORAGE_KEY });
        wiped = true;
      }
    } catch (e) {
      console.warn("[HLOS-176] wipeLegacyBlob failed:", e);
    }

    // 2. Wipe legacy private keys (todas as variações hlos.biometric.privkey.*).
    try {
      const { keys } = await Preferences.keys();
      for (const k of keys) {
        if (k.startsWith(PRIV_KEY_STORAGE_PREFIX)) {
          await Preferences.remove({ key: k });
          wiped = true;
        }
      }
    } catch (e) {
      console.warn("[HLOS-176] wipeLegacyPrivKeys failed:", e);
    }
  } catch (e) {
    // Import ou top-level falhou — silent skip (best-effort).
    console.info("[HLOS-176] wipeLegacyBiometricDataFromPreferences skipped:", e);
  }
  return wiped;
}

// ─────────────────────────────────────────────────────────────────────
// BiometricKeyManager (static API)
// ─────────────────────────────────────────────────────────────────────

  /**
   * HLOS-51: API para gerir unlock biométrico da master key do vault.
   *
   * Conformidade:
   * - CL-08: master key NUNCA em plaintext fora do runtime; cifrada em
   *   SecureStorage (Keychain iOS / Keystore Android).
   * - CC-05: 3+ falhas biométricas → fallback transparente para passphrase.
   * - Princípio V: opt-in voluntário; usuário pode desabilitar a qualquer momento.
   */
  export class BiometricKeyManager {
  /**
   * Setup: gera ECDH keypair, cifra master key, salva blob em SecureStorage.
   *
   * @param masterKey 32 bytes (DEK do vault TS-side)
   * @param authOptions opções do prompt biométrico
   * @returns blob cifrado salvo em SecureStorage
   * @throws se biometric não disponível OU usuário cancelar
   */
  static async setupBiometricUnlock(
    masterKey: Uint8Array,
    authOptions: PluginAuthenticateOptions,
  ): Promise<EncryptedMasterKeyBlob> {
    if (masterKey.byteLength !== 32) {
      throw new Error(
        `[HLOS-51] masterKey deve ter 32 bytes, recebido: ${masterKey.byteLength}`,
      );
    }

    // 1. Gera ECDH keypair efêmero (vai ser cifrado + salvo; chave privada NÃO persiste plaintext)
    const keypair = await crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      true, // extractable: para gerar shared secret
      ["deriveBits"],
    ) as CryptoKeyPair;

    // 2. Prompt biométrico — confirmação do usuário
    // HLOS-57: normalizar reason (PluginAuthenticateOptions tem reason opcional).
    const setupOpts = {
      ...authOptions,
      reason: authOptions.reason ?? "Habilitar desbloqueio biométrico",
    } as Parameters<typeof authenticate>[0];
    const decision: BiometricDecision = await authenticate(setupOpts);
    if (decision.kind !== "ok") {
      throw new Error(
        `[HLOS-51] setup cancelado: biometric decision=${decision.kind}`,
      );
    }

    // 3. Exporta public key
    const publicKeyBytes = await exportEcdhPublicKey(keypair.publicKey);
    const publicKeyBase64 = bytesToBase64(publicKeyBytes);

    // 3a. HLOS-176: cache em memória + persist em SecureStorage (sobrevive force quit,
    //     cifrado pelo hardware — Keychain iOS / Keystore Android).
    await cachePrivateKeyAsync(publicKeyBase64, keypair.privateKey);

    // 4. Deriva shared secret (32 bytes) via ECDH
    const sharedSecret = await deriveSharedSecret(
      keypair.privateKey,
      keypair.publicKey,
    );

    // 5. Importa shared secret como AES-GCM key
    const aesKey = await importAesKey(sharedSecret);

    // 6. Cifra master key com AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await aesGcmEncrypt(aesKey, iv, masterKey);

    // 7. Cria blob e salva em SecureStorage (Keychain/TEE)
    const blob: EncryptedMasterKeyBlob = {
      version: 1,
      publicKey: publicKeyBase64,
      encryptedMasterKey: bytesToBase64(ciphertext),
      iv: bytesToBase64(iv),
      createdAt: Date.now(),
      biometricType: "secure-enclave", // best-effort — detecção completa requer feature opt-in (atualmente não prioritária; ver review HLOS-270)
    };

    await saveBlob(blob);
    resetFailures();
    return blob;
  }

  /**
   * Unlock: prompt biométrico, decifra blob, retorna master key.
   *
   * @param authOptions opções do prompt biométrico
   * @returns master key + outcome
   */
  static async unlockWithBiometric(
    authOptions: PluginAuthenticateOptions,
  ): Promise<BiometricUnlockResult> {
    const blob = await loadBlob();
    if (!blob) {
      return { outcome: "passphrase" };
    }

    try {
      // 1. Prompt biométrico
      // HLOS-57: PluginAuthenticateOptions.reason é opcional (string | undefined),
      // mas AuthenticateOptions local exige reason. Normalizar com default.
      const opts = {
        ...authOptions,
        reason: authOptions.reason ?? "Desbloquear HLOS",
      } as Parameters<typeof authenticate>[0];
      const decision: BiometricDecision = await authenticate(opts);
      if (decision.kind !== "ok") {
        recordFailure(`biometric_${decision.kind}`);
        // HLOS-57 fix: BiometricDecision.kind é "cancel" (sem "ed"), mas o outcome
        // retornado para o caller (AuthState.vue) usa "canceled" (com "ed") como
        // string canônica. Mapeamento aqui.
        return { outcome: decision.kind === "cancel" ? "canceled" : "passphrase" };
      }

      // 2. Importa public key do blob
      const publicKeyBytes = base64ToBytes(blob.publicKey);
      const publicKey = await importEcdhPublicKey(publicKeyBytes);

      // 3. HLOS-176: recupera private key de cache + SecureStorage (sobrevive force quit).
      // Em produção futura, Secure Enclave iOS / Keystore Android deveria gerar
      // o keypair nativamente (non-extractable) e nunca expor a private key ao
      // runtime JS — limitação arquitetural atual: Web Crypto gera a chave com
      // extractable=true e serializa JWK para o SecureStorage (que cifra o
      // blob em hardware-backed storage mas não torna a chave TEE-bound).
      const cachedPrivateKey = await getCachedPrivateKey(blob.publicKey);
      let sharedSecret: Uint8Array;
      if (cachedPrivateKey) {
        sharedSecret = await deriveSharedSecret(cachedPrivateKey, publicKey);
      } else {
        // Fallback (dev only): gera efêmero + deriva. NÃO funciona em produção
        // real porque a private key do setup nunca saiu do enclave.
        // HLOS-176: private key persistida em SecureStorage — este path só
        // ocorre se SecureStorage falhou (ex: storage indisponível).
        console.warn(
          "[HLOS-51] unlock sem private key no cache — " +
            "fallback para keypair efêmero (Sprint 8+ Secure Enclave)",
        );
        const ephemeralKeypair = await crypto.subtle.generateKey(
          { name: "ECDH", namedCurve: "P-256" },
          true,
          ["deriveBits"],
        ) as CryptoKeyPair;
        sharedSecret = await deriveSharedSecret(
          ephemeralKeypair.privateKey,
          publicKey,
        );
      }

      // 4. Decifra master key
      const aesKey = await importAesKey(sharedSecret);
      const iv = base64ToBytes(blob.iv);
      const ciphertext = base64ToBytes(blob.encryptedMasterKey);
      const masterKey = await aesGcmDecrypt(aesKey, iv, ciphertext);

      // 5. Sucesso → reset failure counter
      resetFailures();
      return { outcome: "biometric_ok", masterKey };
    } catch (e) {
      // Erro técnico (decifragem falhou, biometric cancelou, etc)
      recordFailure(`biometric_error:${e instanceof Error ? e.message : String(e)}`);
      return { outcome: "passphrase" };
    }
  }

  /**
   * Check: blob biométrico existe em OPFS?
   */
  static async hasBiometricEnabled(): Promise<boolean> {
    return (await loadBlob()) !== null;
  }

  /**
   * Disable: remove blob cifrado.
   */
  static async disableBiometricUnlock(): Promise<void> {
    const blob = await loadBlob();
    if (blob) {
      await clearCachedPrivateKeyAsync(blob.publicKey);
    }
    await clearBlob();
    resetFailures();
  }

  /**
   * Get blob atual (para diagnóstico / UI).
   */
  static async getCurrentBlob(): Promise<EncryptedMasterKeyBlob | null> {
    return loadBlob();
  }

  /**
   * CC-05: número de falhas consecutivas.
   */
  static getConsecutiveFailures(): number {
    return consecutiveFailures;
  }

  /**
   * CC-05: limpa counter de falhas (após sucesso biométrico ou passphrase).
   */
  static resetFailuresForTesting(): void {
    resetFailures();
  }
}

// ─────────────────────────────────────────────────────────────────────
// Failure counter helpers (module-level)
// ─────────────────────────────────────────────────────────────────────

function recordFailure(reason: string): void {
  consecutiveFailures++;
  // Audit log (CL-09). NUNCA logar master key.
  console.info(
    `[HLOS-51] biometric failure #${consecutiveFailures}/${MAX_FAILURES_BEFORE_FALLBACK}: ${reason}`,
  );
  if (consecutiveFailures >= MAX_FAILURES_BEFORE_FALLBACK) {
    console.info(
      "[HLOS-51] CC-05: max failures reached, fallback to passphrase for next attempts",
    );
  }
}

function resetFailures(): void {
  consecutiveFailures = 0;
}
